import React, { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Divider, Paper, Typography } from '@mui/material';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import Comments from './comments/comments.component';
import ConstructionSection from './construction-section/construction-section.component';
import ManageConstructions from './manage-constructions/manage-constructions.component';
import OrderTotalPrice from './order-total-price/order-total-price.component';
import { convertToSwedishMaterialName } from 'utils/material-enum-to-swedish';
import { getCurrencyValidation } from 'components/order-flow/lab-order/constructions-helper';
import AiAlert from 'components/shared/ai-alert/ai-alert.component';
import InfoText from 'components/shared/info-text.component';

type ConstructionsStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
};
const ConstructionsStep: FC<ConstructionsStepProps> = ({ onBackClick, onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order } = useContext(LabOrderContext);
    const [currentConstructionIndex, setCurrentConstructionIndex] = useState(0);

    const currencyValidation = getCurrencyValidation(order);

    const isValid = () => {
        return (
            currencyValidation.isValid &&
            order.constructions.every((construction) => {
                return construction.isMaterialGridValid && construction.isActionGridValid;
            })
        );
    };

    const aiAssitantMessage = () => {
        if (order.orderForm) {
            let message = '';
            if (order.orderForm.restorationData.length === 0) {
                message += translate('order-forms:AI_NO_PART_ADDED_TO_ORDER') + '\n';
            } else if (order.orderForm.restorationData.length > 1) {
                message +=
                    translate('order-forms:AI_ADDED_PARTS_TO_ORDER', {
                        partCount: order.orderForm.restorationData.length,
                    }) + '\n';
            } else {
                message += translate('order-forms:AI_ADDED_PART_TO_ORDER') + '\n';
            }
            return `${message} ${order.orderForm.restorationData
                .map((restoration) => {
                    let restorationText = ' • ';
                    if (restoration.isWarranty) {
                        restorationText += `${translate('order-forms:WARRANTY')}: `;
                    }
                    restorationText += translate(`orders:${restoration.restorationType}`);
                    if (restoration.material) {
                        restorationText += `, ${convertToSwedishMaterialName(restoration.material)}`;
                    }

                    return restorationText;
                })
                .join('\n')}`;
        }
        return '';
    };

    return (
        <Fragment>
            <Box>
                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h1">{translate('orders:CONSTRUCTIONS')}</Typography>
                    <Box>
                        <OrderTotalPrice />
                    </Box>
                </Box>
                <Box mb={2}>
                    <Divider />
                </Box>

                {currencyValidation.errorKey ? (
                    <Box mb={2}>
                        <Alert severity="error" data-test="constructions-step.currency-alert">
                            {translate(`orders:${currencyValidation.errorKey}`, currencyValidation.values)}
                        </Alert>
                    </Box>
                ) : undefined}

                {order.orderForm?.restorationData ? (
                    <Box mb={2}>
                        <AiAlert title={translate('common:AI_ASSISTANT')} message={aiAssitantMessage()} />
                    </Box>
                ) : undefined}

                <Box mb={2}>
                    <ManageConstructions onSelectedConstructionChange={(index) => setCurrentConstructionIndex(index)} />
                </Box>
                <Paper>
                    <Box p={2}>
                        <ConstructionSection constructionIndex={currentConstructionIndex} />
                    </Box>
                </Paper>

                <Box mb={4}>
                    <Divider />
                </Box>

                <Box mb={4}>
                    <Box mb={2}>
                        <Typography variant="h3" gutterBottom>
                            {translate('orders:COMMENT')}
                        </Typography>
                    </Box>
                    <Box>
                        {order.scannerCase ? (
                            <InfoText text={translate('scan-cases:SCAN_CASE_COMMENT_INFO')} />
                        ) : undefined}
                        <Comments />
                    </Box>
                </Box>

                <Box display="flex" mt={2}>
                    <Box flexGrow={1} textAlign="left">
                        <Button type="button" onClick={onBackClick} data-test="constructions-step.back">
                            {translate('common:BACK')}
                        </Button>
                    </Box>
                    <Box>
                        <Button
                            disabled={!isValid()}
                            type="button"
                            onClick={onNextClick}
                            variant="contained"
                            data-test="constructions-step.next"
                        >
                            {translate('common:NEXT_STEP')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Fragment>
    );
};

export default ConstructionsStep;
